import { useEffect, useMemo, useState } from 'react';
import {
  CLUTCH_OPERATION_MODE_KEYS,
  convertData,
  convertIntervalToSecond,
  createBucketLookup,
  generateTimestampData,
  getPresentOperationModeKeys,
} from './helperFunction';
import { generateReportOperationModeColumns } from './tableColumn';
import useGroup from '../../../zustand/useGroup';
import useAppliedReportFilter from '../../../hooks/useAppliedReportFilter';
import useReportLog from '../../../hooks/useReportLog';
import { Collapse, Empty, Grid, Table } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import TableConfiguration from './tableConfiguration';
import usePermission from '../../../hooks/usePermission';
import styled from 'styled-components';
import { useVT } from 'virtualizedtableforantd4';
import SummaryOperationModeTable from './summaryOperationModeTable';

const windowHeight = window.innerHeight;
export interface RowData {
  [key: string]: string | number | null;
}

interface TableReportOperationModeProps {
  hasOperationModeData?: boolean;
}

const TableReportOperationMode: React.FC<TableReportOperationModeProps> = ({ hasOperationModeData = true }) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const { isUserHasPermission } = usePermission();
  const { filter, timezone } = useAppliedReportFilter();
  const {
    setChecklistColumn,
    checklistColumn,
    loadingLog: loading,
    logs,
  } = useReportLog();
  const [columns, setColumns] = useState<any>([]);
  const [openSections, setOpenSections] = useState<string[]>([]);
  const [vt] = useVT(() => ({ scroll: { x: 700, y: windowHeight - 300 } }), []);

  const hasEngineOperationMode = useMemo(() => {
    return (
      getPresentOperationModeKeys(logs?.operation_mode, [
        'port',
        'starboard',
        'center',
      ]).length > 0
    );
  }, [logs]);

  const hasClutchOperationMode = useMemo(() => {
    return (
      getPresentOperationModeKeys(logs?.operation_mode, CLUTCH_OPERATION_MODE_KEYS)
        .length > 0
    );
  }, [logs]);

  useEffect(() => {
    if (logs && Object.keys(logs).length) {
      const markVisible = (item: any) => {
        if (item.children) {
          return {
            ...item,
            hidden: false,
            children: item.children.map((child: any) => ({
              ...child,
              hidden: false,
            })),
          };
        }

        return {
          ...item,
          hidden: false,
        };
      };

      setColumns(
        generateReportOperationModeColumns(logs, 'combined').map(markVisible),
      );
    }
  }, [logs]);

  useEffect(() => {
    if (columns && columns.length) {
      const checklist: string[] = [];

      columns.forEach((item: any) => {
        if (item.children) {
          checklist.push(item.key);
          item.children.forEach((child: any) => {
            checklist.push(child.key);
          });
        } else {
          checklist.push(item.key);
        }
      });

      setChecklistColumn(checklist);
    }
  }, [columns]);

  const columnsFiltered = useMemo(() => {
    const res = columns
      .filter((item: any) => {
        if (checklistColumn.includes(item.key)) {
          return item;
        } else if (item.children) {
          const isParentExist = checklistColumn.find(
            (columnKey) => item.key === columnKey
          );
          if (isParentExist) {
            return {
              ...item,
              children: item.children.filter((child: any) =>
                checklistColumn.includes(child.key)
              ),
            };
          }
        }
      })
      .map((item: any, idx: number) => {
        if (item.children && item.children.length) {
          return {
            ...item,
          };
        } else {
          return item;
        }
      });
    return res;
  }, [columns, checklistColumn]);

  const dataSource = useMemo(() => {
    if (logs && Object.keys(logs).length) {
      let intervalInSecond = convertIntervalToSecond(filter.interval);

      const containerTimestamp = generateTimestampData({
        startTime: filter.startAt,
        endTime: filter.endAt,
        interval: filter.interval,
        intervalInSecond,
        timezone,
      });

      const operationModeLookup = createBucketLookup(logs.operation_mode);

      const results = containerTimestamp.map((timestamp) => {
        let opMode;
        if (logs.operation_mode) {
          opMode = operationModeLookup.get(timestamp);
        }

        return {
          timestamp,
          dateTime: dayjs(timestamp * 1000)
            .utcOffset(timezone * 60)
            .format('DD MMM YYYY HH:mm'),
          port_Rpm: convertData(opMode?.port?.rpm),
          port_Mode: opMode?.port?.mode,
          port_ConsRate: convertData(opMode?.port?.consRate),
          starboard_Rpm: convertData(opMode?.starboard?.rpm),
          starboard_Mode: opMode?.starboard?.mode,
          starboard_ConsRate: convertData(opMode?.starboard?.consRate),
          center_Rpm: convertData(opMode?.center?.rpm),
          center_Mode: opMode?.center?.mode,
          center_ConsRate: convertData(opMode?.center?.consRate),
          clutchPort_Clutch: convertData(opMode?.clutchPort?.clutch),
          clutchPort_Mode: opMode?.clutchPort?.mode,
          clutchPort_ConsRate: convertData(opMode?.clutchPort?.consRate),
          clutchStarboard_Clutch: convertData(opMode?.clutchStarboard?.clutch),
          clutchStarboard_Mode: opMode?.clutchStarboard?.mode,
          clutchStarboard_ConsRate: convertData(opMode?.clutchStarboard?.consRate),
          clutchCenter_Clutch: convertData(opMode?.clutchCenter?.clutch),
          clutchCenter_Mode: opMode?.clutchCenter?.mode,
          clutchCenter_ConsRate: convertData(opMode?.clutchCenter?.consRate),
        };
      });

      return results;
    }
  }, [filter.endAt, filter.interval, filter.startAt, logs, timezone]);

  const sectionKey = [
    hasEngineOperationMode || !hasClutchOperationMode ? 'engine' : '',
    hasClutchOperationMode ? 'clutch' : '',
  ]
    .filter(Boolean)
    .join('|');

  useEffect(() => {
    setOpenSections(sectionKey ? sectionKey.split('|') : []);
  }, [sectionKey]);

  const summarySections = [
    ...(hasEngineOperationMode || !hasClutchOperationMode
      ? [
          {
            key: 'engine',
            label: 'Operation Mode',
            children: <SummaryOperationModeTable />,
          },
        ]
      : []),
    ...(hasClutchOperationMode
      ? [
          {
            key: 'clutch',
            label: 'Operation Mode Clutch',
            children: <SummaryOperationModeTable source='clutch' />,
          },
        ]
      : []),
  ];

  return (
    <React.Fragment>
      {!hasOperationModeData ? (
        <EmptyStateContainer>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <EmptyDescription>
                <span>Operation Mode is enabled for this asset, but no operation mode data is configured.</span>
                <span>Please configure the operation mode settings for this vessel.</span>
              </EmptyDescription>
            }
          />
        </EmptyStateContainer>
      ) : (
        <>
          <SummaryRow>
            {summarySections.map((section) => (
              <OperationModeCollapse
                key={section.key}
                activeKey={openSections.includes(section.key) ? [section.key] : []}
                onChange={(key) => {
                  const keys = Array.isArray(key) ? key : [key];
                  setOpenSections((current) => {
                    const withoutSection = current.filter(
                      (item) => item !== section.key,
                    );
                    return keys.length
                      ? [...withoutSection, section.key]
                      : withoutSection;
                  });
                }}
                items={[section]}
              />
            ))}
          </SummaryRow>

          <MobileTableFrame>
            <Table
              loading={loading}
              className='table-report-operation-mode'
              columns={columnsFiltered}
              dataSource={dataSource}
              size='small'
              bordered
              pagination={{
                pageSize: 500,
              }}
              components={isMobile ? undefined : vt}
              scroll={
                isMobile ? { x: 'max-content' } : { x: 700, y: windowHeight - 300 }
              }
            />
          </MobileTableFrame>
        </>
      )}
    </React.Fragment>
  );
};

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
  margin-bottom: 16px;

  table {
    margin-top: 0;
  }

  @media (max-width: 767px) {
    margin: 0 20px 12px;
  }
`;

const OperationModeCollapse = styled(Collapse)`
  background: transparent;

  .ant-collapse-item {
    overflow: hidden;
    border: 1px solid #e8ecf3;
    border-radius: 8px;
    background: #ffffff;
  }

  .ant-collapse-header {
    font-weight: 600;
  }
`;

const EmptyStateContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  background-color: #fafafa;
  border-radius: 8px;
  margin: 16px 0;

  @media (max-width: 767px) {
    margin: 12px 20px 0;
    min-height: 240px;
    border: 1px solid #e8ecf3;
    background-color: #ffffff;
  }
`;

const EmptyDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
  color: #666;
`;

const ContainerFilterTable = styled.div`
  display: flex;
  gap: 10px;
  justify-content: space-between;
`;

const Wrapper = styled.div`
  display: flex;
  gap: 10px;
`;

const MobileTableFrame = styled.div`
  @media (max-width: 767px) {
    margin: 12px 20px 0;
    overflow: hidden;
    border: 1px solid #e8ecf3;
    border-radius: 8px;
    background: #ffffff;

    .ant-table {
      font-size: 12px;
    }

    .ant-table-thead > tr > th {
      white-space: nowrap;
      background: #e8ebf3;
      color: #1e1e1e;
      font-size: 12px;
      font-weight: 600;
    }

    .ant-table-tbody > tr > td {
      white-space: nowrap;
      font-size: 12px;
    }

    .ant-pagination {
      margin: 12px;
    }
  }
`;

export default TableReportOperationMode;
